import React, { Fragment, useEffect } from 'react';
import { Menu, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import {
	CalendarDaysIcon,
	ChevronDownIcon,
	CloudArrowUpIcon,
	RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';

import { AvatarGenerated } from '../Avatar';

import useLocal from '../../hooks/useLocal';
import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';

import { getUserFullName } from '../helpers/functions';
import { getEvents } from '../../api';

const DOCUMENTS_MODULE_ID = process.env.REACT_APP_MODULE_ID_DOCUMENTS;

const UserMenu = ({ navigation, avatarImage }) => {
	const navigate = useNavigate();

	const { get } = useLocal();
	const user = get('user');

	const { setOpen: openAddEventModal } = useModal('eventAdd');
	const { setOpen: openUploadDocumentsModal } = useModal('uploadDocuments');

	const eventsPayload = {
		status: ['ESTIMATED'],
		endDate: moment().add(30, 'd').toDate(),
	};

	const eventsExpiredScheduledPayload = {
		status: ['SCHEDULED'],
		endDate: moment().subtract(1, 'd').startOf('d').toDate(),
	};

	const {
		value: estimatedEvents,
		execute: refreshEvents,
		status,
	} = useAsync(getEvents, {
		defaultPayload: eventsPayload,
	});

	const { value: overdueScheduledEvents, execute: refreshExpiredEvents } =
		useAsync(getEvents, {
			defaultPayload: eventsExpiredScheduledPayload,
		});

	useEffect(() => {
		if (!open) {
			refreshEvents(eventsPayload, null, { silent: true });
			refreshExpiredEvents(eventsExpiredScheduledPayload, null, {
				silent: true,
			});
		}
	}, [open]);

	const combinedEvents = [
		...(estimatedEvents?.rows || []),
		...(overdueScheduledEvents?.rows || []),
	];

	return (
		<>
			<Menu as="div" className="relative ml-3">
				<div>
					<Menu.Button className="flex max-w-xs items-center rounded-full bg-white text-sm">
						<RocketLaunchIcon className="w-6 h-6 text-gray-400" />
					</Menu.Button>
				</div>
				<Transition
					as={Fragment}
					enter="transition ease-out duration-100"
					enterFrom="transform opacity-0 scale-95"
					enterTo="transform opacity-100 scale-100"
					leave="transition ease-in duration-75"
					leaveFrom="transform opacity-100 scale-100"
					leaveTo="transform opacity-0 scale-95"
				>
					<Menu.Items className="absolute right-0 z-10 mt-2 w-64 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
						{[
							{
								name: 'Schedule Service',
								onClick: () => openAddEventModal(),
								Icon: CalendarDaysIcon,
							},
							{
								name: 'Upload Document',
								onClick: () =>
									openUploadDocumentsModal({ moduleId: DOCUMENTS_MODULE_ID }),
								Icon: CloudArrowUpIcon,
							},
						].map((item) => (
							<Menu.Item key={item.name}>
								{({ active }) => (
									<a
										href={item.href}
										onClick={() => {
											if (item.path) {
												navigate(item.path);
											}
											if (typeof item.onClick === 'function') {
												item.onClick();
											}
										}}
										className={classNames(
											active ? 'bg-gray-100' : '',
											'group flex items-center px-4 py-2 text-xs cursor-pointer text-gray-500 hover:text-gray-600'
										)}
									>
										{item?.Icon ? (
											<item.Icon
												className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
												aria-hidden="true"
											/>
										) : null}
										{item.name}
									</a>
								)}
							</Menu.Item>
						))}
					</Menu.Items>
				</Transition>
			</Menu>
			{/*}
			<Menu as="div" className="relative ml-3 mt-1">
				<div>
					<Menu.Button
						className="relative flex max-w-xs items-center rounded-full bg-white text-sm mr-3"
						onClick={() => refreshEvents(eventsPayload, null, { silent: true })}
					>
						<span className="sr-only">Open user menu</span>

						<CalendarDaysIcon className="w-6 h-6 ml-3 text-gray-400" />
						<div class="absolute inline-flex items-center justify-center w-5 h-5 text-xxs text-white bg-red-500 border-1 border-white rounded-full -top-2 -right-2 dark:border-gray-900">
							{combinedEvents?.length || 0}
						</div>
					</Menu.Button>
				</div>
				<Transition
					as={Fragment}
					enter="transition ease-out duration-100"
					enterFrom="transform opacity-0 scale-95"
					enterTo="transform opacity-100 scale-100"
					leave="transition ease-in duration-75"
					leaveFrom="transform opacity-100 scale-100"
					leaveTo="transform opacity-0 scale-95"
				>
					<div className="px-3 py-2 absolute right-0 z-10 my-2 w-64 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
						<div className="text-xs font-medium my-2">Calendar Reminders</div>
						{combinedEvents?.length ? (
							combinedEvents
								?.sort(
									(a, b) =>
										new Date(a[dateKey[a.status]]) -
										new Date(b[dateKey[b.status]])
								)
								.map((event) => {
									const engineer =
										event?.service?.engineer?.firstName ||
										event?.service?.engineer?.lastName
											? getUserFullName(event.service.engineer)
											: event?.service?.engineer?.companyName;

									const title = event?.service?.engineer
										? `${engineer}${
												event?.service?.engineer?.company
													? ` from ${event?.service?.engineer?.company?.companyName}`
													: ''
										  }`
										: event?.service?.name || event?.description;

									const date = moment(
										event.completedDate ||
											event.scheduledDate ||
											event.estimatedDate
									).startOf('d');

									const statusMapping = {
										ESTIMATED: `Estimated for ${moment(
											event.estimatedDate
										).format('dddd, MMM Do')}`,
										SCHEDULED: `Scheduled for ${moment(
											event.scheduledDate
										).format('dddd, MMM Do')}`,
									}[event.status];

									const isOverdue = date.isBefore(moment().endOf('d'));

									return (
										<div
											className=" cursor-pointer mb-2"
											onClick={() =>
												openServiceOverview({ id: event?.service?.id })
											}
										>
											{isOverdue ? (
												<Badge isBeta status="error" className="w-full">
													<div className="text-xxs flex items-center">
														<ExclamationTriangleIcon className="w-5 h-5 text-red-600/70 mr-1" />
														<div className="ml-2">
															<div className="font-medium">{`Overdue: ${title}`}</div>
															<div className="font-light">{`${statusMapping}`}</div>
														</div>
													</div>
												</Badge>
											) : (
												<Badge isBeta className="w-full">
													<div className="text-xxs flex items-center">
														<InformationCircleIcon className="w-5 h-5 text-gray-600/70 mr-1" />

														<div className="ml-2">
															<div className="font-medium">{`Upcoming: ${title}`}</div>
															<div className="font-light">{`${statusMapping}`}</div>
														</div>
													</div>
												</Badge>
											)}
										</div>
									);
								})
						) : (
							<div className="text-xs text-gray-600 text-center py-2 px-3">
								No reminders
							</div>
						)}
					</div>
				
				</Transition>
			</Menu>*/}
			<Menu as="div" className="relative ml-4">
				<div>
					<Menu.Button className="flex max-w-xs items-center rounded-full bg-white text-sm">
						<span className="sr-only">Open user menu</span>
						{avatarImage ? (
							<img className="h-8 w-8 rounded-full" src={avatarImage} alt="" />
						) : (
							<>
								<AvatarGenerated size="xs" id={getUserFullName(user)} />
								<ChevronDownIcon className="w-4 h-4 ml-3" />
							</>
						)}
					</Menu.Button>
				</div>
				<Transition
					as={Fragment}
					enter="transition ease-out duration-100"
					enterFrom="transform opacity-0 scale-95"
					enterTo="transform opacity-100 scale-100"
					leave="transition ease-in duration-75"
					leaveFrom="transform opacity-100 scale-100"
					leaveTo="transform opacity-0 scale-95"
				>
					<Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
						{navigation.map((item) => (
							<Menu.Item key={item.name}>
								{({ active }) => (
									<a
										href={item.href}
										onClick={() => {
											if (item.path) {
												navigate(item.path);
											}
											if (typeof item.onClick === 'function') {
												item.onClick();
											}
										}}
										className={classNames(
											active ? 'bg-gray-100' : '',
											'block px-4 py-2 text-sm text-gray-700 cursor-pointer'
										)}
									>
										{item.name}
									</a>
								)}
							</Menu.Item>
						))}
					</Menu.Items>
				</Transition>
			</Menu>
		</>
	);
};

export default UserMenu;
